import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs/operators';

import { AuthActions } from '@shared/store/auth.actions';
import { StoreActions } from '@shared/store/store.actions';

import { StatisticsApiService } from '../services/statistics-api.service';
import { StatisticsActions } from './statistics.actions';
import { environment } from '@environment/environment';

export interface StatisticsStateModel {
  /**
   * De-duplication keys for events already dispatched in the current
   * session. Cleared on logout / reset / explicit `ResetSession`.
   * Shape: `<type>:<code>:<name>` — `name` is included so the
   * `customers` trigger can fire twice for the same agency code (one
   * event for the broker name, one for the agency name).
   */
  sentKeys: string[];
}

export const STATISTICS_STATE_TOKEN =
    new StateToken<StatisticsStateModel>('StatisticsState');

const defaultState: StatisticsStateModel = {
  sentKeys: [],
};

@State<StatisticsStateModel>({
  name: STATISTICS_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class StatisticsState {

  private _statisticsService = inject(StatisticsApiService);

  @Selector()
  static sentKeys(state: StatisticsStateModel): string[] {
    return state.sentKeys;
  }

  @Action(StatisticsActions.Track)
  track(
      ctx: StateContext<StatisticsStateModel>,
      action: StatisticsActions.Track,
  ) {
    const { sentKeys } = ctx.getState();
    const dedupKey = action.payload.dedupKey;

    if (!environment.statistics || sentKeys.includes(dedupKey)) {
      return;
    }

    ctx.patchState({
      sentKeys: [...sentKeys, dedupKey],
    });

    return this._statisticsService
               .put$(action.payload)
               .pipe(
                   tap(() => {
                     // fire-and-forget: nothing to mutate on success
                   }),
               );
  }

  @Action(StatisticsActions.ResetSession)
  resetSession(ctx: StateContext<StatisticsStateModel>) {
    ctx.patchState({
      sentKeys: defaultState.sentKeys,
    });
  }

  @Action([
    StoreActions.ResetLoadedState,
    StatisticsActions.ResetLoadedState,
  ])
  resetLoadedState(ctx: StateContext<StatisticsStateModel>) {
    ctx.patchState({
      sentKeys: defaultState.sentKeys,
    });
  }

  @Action([
    StatisticsActions.ResetState,
    StoreActions.ResetStore,
    AuthActions.Unauthorized,
  ])
  resetState(ctx: StateContext<StatisticsStateModel>) {
    ctx.setState({
      ...defaultState,
    });
  }
}
